import { ArrowPathIcon, PencilIcon } from "@heroicons/react/24/outline";
import {
  Avatar,
  Button,
  Chip,
  Input,
  Pagination,
  Select,
  SelectItem,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
} from "@heroui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  fetchCountries,
  fetchTrainingDisciplines,
  fetchWeightClasses,
  getFighters,
} from "../../services/api";
import { Profile } from "../../types/types";
import { getStatusColor } from "../../utils/fightersAndManagers";
import {
  formatItemName, 
  formatSportName,
  formatWeightClassName,
  getInitials,
} from "../../utils/utilMethods";
import EditProfileStatus from "../adminActions/EditProfileStatus";

const Fighters: React.FC = () => {
  const { t } = useTranslation("fighters");
  const [fighters, setFighters] = useState<Profile[]>([]);
  const [searchName, setSearchName] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedSport, setSelectedSport] = useState("");
  const [selectedWeight, setSelectedWeight] = useState("");
  const [selectedTitle, setSelectedTitle] = useState("");
  const [locations, setLocations] = useState<string[]>([]);
  const [sports, setSports] = useState<string[]>([]);
  const [weightClasses, setWeightClasses] = useState<string[]>([]);
  const [selectedFighter, setSelectedFighter] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedGender, setSelectedGender] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 10;

  const [editStatusModalOpen, setEditStatusModalOpen] = useState(false);
  const [role, setRole] = useState("");
  const navigate = useNavigate();
  const profileType = JSON.parse(
    localStorage.getItem("loginResponse") || "{}",
  ).profileType;

  const titleOptions = [t("all_titles"), t("yes"), t("no")];

  const getUserRole = (): string => {
    const userJson = localStorage.getItem("loginResponse");
    const user = userJson ? JSON.parse(userJson) : null;
    return user?.profileType || "";
  };

  useEffect(() => {
    setRole(getUserRole());

    const loadLocations = async () => {
      try {
        setLocations([t("all_locations"), ...(await fetchCountries())]);
      } catch (err) {
        setError(t("error_countries"));
      }
    };
    loadLocations();
  }, [t]);

  const fetchFightersData = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        name: searchName.trim() || undefined,
        location:
          selectedLocation === t("all_locations")
            ? undefined
            : selectedLocation || undefined,
        trainingDiscipline:
          selectedSport === t("all_sports")
            ? undefined
            : selectedSport || undefined,
        weightClass:
          selectedWeight === t("all_weight_classes")
            ? undefined
            : selectedWeight || undefined,
        titleHolder:
          selectedTitle === t("all_titles")
            ? undefined
            : selectedTitle === t("yes")
              ? true
              : selectedTitle === t("no")
                ? false
                : undefined,
        gender: selectedGender || undefined,
        page,
        pageSize,
      };

      const response = await getFighters(payload);

      if (response) {
        setFighters(response.data);
        setTotalRecords(response.total);
        setTotalPages(response.totalPages);
      }
    } catch (err) {
      setError(t("error_fighters"));
      setFighters([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFightersData();
  }, [
    searchName,
    selectedLocation,
    selectedSport,
    selectedWeight,
    selectedTitle,
    selectedGender,
    page,
    t,
  ]);

  const loadSportsIfNeeded = async () => {
    if (sports.length === 0) {
      try {
        setSports([t("all_sports"), ...(await fetchTrainingDisciplines())]);
      } catch (err) {
        setError(t("error_sports"));
      }
    }
  };

  const loadWeightClassesIfNeeded = async () => {
    if (weightClasses.length === 0) {
      try {
        setWeightClasses([
          t("all_weight_classes"),
          ...(await fetchWeightClasses()),
        ]);
      } catch (err) {
        setError(t("error_weight_classes"));
      }
    }
  };

  const handleRowClick = (fighter: Profile) => {
    navigate(`/profile?profileId=${fighter.id}`);
  };

  const handleEditProfile = (event: React.MouseEvent, fighter: Profile) => {
    event.stopPropagation();
    setSelectedFighter(fighter);
    setEditStatusModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditStatusModalOpen(false);
    setSelectedFighter(null);
    fetchFightersData();
  };

  const resetFilters = () => {
    setSearchName("");
    setSelectedLocation("");
    setSelectedSport("");
    setSelectedWeight("");
    setSelectedTitle("");
    setSelectedGender("");
    setPage(0);
  };

  const ChipGrid: React.FC<{ items: string[]; maxChips?: number }> = ({
    items,
    maxChips = 8,
  }) => {
    if (!items || items.length === 0)
      return <span className="text-xs text-gray-400">{t("na")}</span>;

    const displayItems = items.slice(0, maxChips);
    const hasMore = items.length > maxChips;

    return (
      <div className="flex flex-wrap gap-1 max-w-[180px]">
        {displayItems.map((item, index) => (
          <Chip
            key={index}
            size="sm"
            variant="flat"
            color="default"
            className="text-[10px] px-1 py-0 h-4 min-h-4 leading-tight bg.gray-100"
          >
            {formatItemName(item)}
          </Chip>
        ))}
        {hasMore && (
          <Chip
            size="sm"
            variant="flat"
            color="default"
            className="text-[10px] px-1 py-0 h-4 min-h-4 leading-tight bg-gray-50"
          >
            +{items.length - maxChips}
          </Chip>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-screen flex flex-col">
      <div className="sticky top-0 z-10 border-b bg-white border-gray-200 p-2 flex-shrink-0">
        <div className="max-w-full mx-auto">
          <div className="flex items-center gap-3 flex-wrap bg-white">
            <Input
              placeholder={t("search_name_placeholder")}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="w-64 min-w-64"
              size="sm"
            />
            <Select
              placeholder={t("location_placeholder")}
              selectedKeys={selectedLocation ? [selectedLocation] : []}
              onSelectionChange={(keys) =>
                setSelectedLocation((Array.from(keys)[0] as string) || "")
              }
              className="w-48 min-w-48 bg-white"
              size="sm"
            >
              {locations.map((location) => (
                <SelectItem key={location}>{location}</SelectItem>
              ))}
            </Select>
            <Select
              placeholder={t("combat_sport_placeholder")}
              selectedKeys={selectedSport ? [selectedSport] : []}
              onSelectionChange={(keys) =>
                setSelectedSport((Array.from(keys)[0] as string) || "")
              }
              onOpenChange={(isOpen) => isOpen && loadSportsIfNeeded()}
              className="w-48 min-w-48"
              size="sm"
            >
              {sports.map((sport) => (
                <SelectItem key={sport}>{formatSportName(sport)}</SelectItem>
              ))}
            </Select>
            <Select
              placeholder={t("weight_class_placeholder")}
              selectedKeys={selectedWeight ? [selectedWeight] : []}
              onSelectionChange={(keys) =>
                setSelectedWeight((Array.from(keys)[0] as string) || "")
              }
              onOpenChange={(isOpen) => isOpen && loadWeightClassesIfNeeded()}
              className="w-48 min-w-48"
              size="sm"
            >
              {weightClasses.map((weightClass) => (
                <SelectItem key={weightClass}>
                  {formatWeightClassName(weightClass)}
                </SelectItem>
              ))}
            </Select>
            <Select
              placeholder={t("title_holder_placeholder")}
              selectedKeys={selectedTitle ? [selectedTitle] : []}
              onSelectionChange={(keys) =>
                setSelectedTitle((Array.from(keys)[0] as string) || "")
              }
              className="w-48 min-w-48"
              size="sm"
            >
              {titleOptions.map((option) => (
                <SelectItem key={option}>{option}</SelectItem>
              ))}
            </Select>
            <Select
              placeholder={t("gender_placeholder")}
              selectedKeys={selectedGender ? [selectedGender] : []}
              onSelectionChange={(keys) =>
                setSelectedGender((Array.from(keys)[0] as string) || "")
              }
              className="w-40 min-w-40"
              size="sm"
            >
              <SelectItem key="MALE">{t("male")}</SelectItem>
              <SelectItem key="FEMALE">{t("female")}</SelectItem>
              <SelectItem key="OTHERS">{t("others")}</SelectItem>
            </Select>

            <Tooltip content={t("reset_filters_tooltip")}>
              <Button
                isIconOnly
                variant="light"
                onClick={resetFilters}
                className="h-10 w-10 text-gray-600 hover:text-gray-800"
                aria-label={t("reset_filters_tooltip")}
              >
                <ArrowPathIcon className="w-5 h-5" />
              </Button>
            </Tooltip>
            {loading && (
              <div className="flex items-center ml-2">
                <Spinner size="sm" color="primary" />
                <span className="ml-2 text-sm text-gray-600">
                  {t("loading_text")}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1">
        <div className="p-2 max-w-full mx-auto">
          {error && <p className="text-red-600 mb-4">{error}</p>}

          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            {loading ? (
              <div className="p-4">
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center space-x-4 p-4 border-b border-gray-100"
                    >
                      <div className="w-10 h-10 bg-gray-200 rounded-full shimmer"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded shimmer w-1/4"></div>
                        <div className="h-3 bg-gray-200 rounded shimmer w-1/3"></div>
                      </div>
                      <div className="h-4 bg-gray-200 rounded shimmer w-20"></div>
                      <div className="h-4 bg-gray-200 rounded shimmer w-16"></div>
                      <div className="h-6 bg-gray-200 rounded shimmer w-12"></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="fade-in">
                <Table
                  aria-label={t("table_column_fighter")}
                  className="min-w-full"
                  removeWrapper
                  classNames={{
                    wrapper: "overflow-auto custom-scrollbar",
                    table: "min-w-full",
                  }}
                >
                  <TableHeader>
                    <TableColumn className="text-left text-xs font-small text-gray-500 min-w-[200px]">
                      {t("table_column_fighter")}
                    </TableColumn>
                    <TableColumn className="text-left text-xs font-small text-gray-500 min-w-[120px]">
                      {t("table_column_phone")}
                    </TableColumn>
                    <TableColumn className="text-left text-xs font-small text-gray-500 min-w-[120px]">
                      {t("table_column_location")}
                    </TableColumn>
                    <TableColumn className="text-left text-xs font-small text-gray-500 min-w-[200px]">
                      {t("table_column_disciplines")}
                    </TableColumn>
                    <TableColumn className="text-left text-xs font-small text-gray-500 min-w-[200px]">
                      {t("table_column_weight_class")}
                    </TableColumn>
                    <TableColumn className="text-left text-xs font-small text-gray-500 min-w-[100px]">
                      {t("table_column_gender")}
                    </TableColumn>
                    <TableColumn className="text-center text-xs font-small text-gray-500 min-w-[80px]">
                      {t("table_column_title")}
                    </TableColumn>
                    <TableColumn className="text-center text-xs font-small text-gray-500 min-w-[100px]">
                      {t("table_column_status")}
                    </TableColumn>
                    <TableColumn className="text-center text-xs font-small text-gray-500 min-w-[80px]">
                      {t("table_column_actions")}
                    </TableColumn>
                  </TableHeader>
                  <TableBody
                    items={fighters.map((fighter) => ({
                      key: fighter.id,
                      fighter,
                    }))}
                    emptyContent={t("table_empty_content")}
                  >
                    {(item) => (
                      <TableRow
                        key={item.key}
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest("button"))
                            return;
                          handleRowClick(item.fighter);
                        }}
                        className="cursor-pointer hover:bg-gray-50 border-b border-gray-100 fade-in"
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="relative flex-shrink-0">
                              <Avatar
                                src={item.fighter.profileImage}
                                alt={`${item.fighter.firstName} ${item.fighter.lastName}`}
                                name={getInitials(
                                  item.fighter.firstName,
                                  item.fighter.lastName,
                                )}
                                size="sm"
                                imgProps={{
                                  referrerPolicy: "no-referrer",
                                  crossOrigin: "anonymous",
                                }}
                              />
                              {item.fighter?.verified && (
                                <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-white rounded-full overflow-hidden">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 48 48"
                                    className="w-full h-full"
                                  >
                                    <polygon
                                      fill="#42a5f5"
                                      points="29.62,3 33.053,8.308 39.367,8.624 39.686,14.937 44.997,18.367 42.116,23.995 45,29.62 39.692,33.053 39.376,39.367 33.063,39.686 29.633,44.997 24.005,42.116 18.38,45 14.947,39.692 8.633,39.376 8.314,33.063 3.003,29.633 5.884,24.005 3,18.38 8.308,14.947 8.624,8.633 14.937,8.314 18.367,3.003 23.995,5.884"
                                    />
                                    <polygon
                                      fill="#fff"
                                      points="21.396,31.255 14.899,24.76 17.021,22.639 21.428,27.046 30.996,17.772 33.084,19.926"
                                    />
                                  </svg>
                                </span>
                              )}
                            </div>

                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900 text-sm truncate">
                                {item.fighter.firstName} {item.fighter.lastName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {(() => {
                                  const loginResponse = JSON.parse(
                                    localStorage.getItem("loginResponse") ||
                                      "{}",
                                  );
                                  const isAdmin =
                                    loginResponse.profileType === "ADMIN";

                                  if (isAdmin) {
                                    return item.fighter?.username
                                      ? item.fighter.username.length > 23
                                        ? `${item.fighter.username.slice(
                                            0,
                                            23,
                                          )}...`
                                        : item.fighter.username
                                      : "-";
                                  }
                                  return "";
                                })()}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-gray-700 truncate">
                            {item.fighter.phone}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-gray-700 truncate">
                            {item.fighter.residenceCountry}
                          </div>
                        </TableCell>
                        <TableCell>
                          <ChipGrid
                            items={
                              item.fighter.fighter?.trainingDisciplines || []
                            }
                            maxChips={4}
                          />
                        </TableCell>
                        <TableCell>
                          <ChipGrid
                            items={item.fighter.fighter?.weightClasses || []}
                            maxChips={4}
                          />
                        </TableCell>
                        <TableCell>
                          <ChipGrid
                            items={
                              item.fighter.gender
                                ? [t(item.fighter.gender)]
                                : []
                            }
                            maxChips={1}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Chip
                            size="sm"
                            variant="flat"
                            color={
                              item.fighter.fighter?.titleHolder
                                ? "success"
                                : "default"
                            }
                          >
                            {item.fighter.fighter?.titleHolder
                              ? t("yes")
                              : t("no")}
                          </Chip>
                        </TableCell>
                        <TableCell className="text-center">
                          <Chip
                            size="sm"
                            variant="flat"
                            color={getStatusColor(item.fighter.profileStatus)}
                          >
                            {item.fighter.profileStatus}
                          </Chip>
                        </TableCell>
                        <TableCell className="text-center">
                          {role === "ADMIN" ? (
                            <Tooltip content={t("edit_fighter_tooltip")}>
                              <Button
                                isIconOnly
                                variant="light"
                                onClick={(e) =>
                                  handleEditProfile(e, item.fighter)
                                }
                                className="h-8 w-8 text-blue-600 hover:bg-blue-50"
                                aria-label={t("edit_fighter_tooltip")}
                              >
                                <PencilIcon className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          {!loading && (fighters.length > 0 || totalPages > 1) && (
            <div className="fixed bottom-0 right-0 left-[80px] flex flex-col items-center justify-center py-4 z-50">
              <div className="text-sm text-gray-600 mb-2">
                {`Showing ${page * pageSize + 1} to ${Math.min(
                  (page + 1) * pageSize,
                  totalRecords,
                )} of ${totalRecords} fighters`}
              </div>
              <Pagination
                total={totalPages}
                page={page + 1}
                onChange={(newPage) => setPage(newPage - 1)}
                showControls
                color="primary"
                className="text-sm"
              />
            </div>
          )}

          {editStatusModalOpen && selectedFighter && (
            <EditProfileStatus
              isOpen={editStatusModalOpen}
              onClose={handleCloseEditModal}
              profile={selectedFighter}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Fighters;
